/**
 * How long a campaign actually takes to go out.
 *
 * THE FACT THIS MODULE EXISTS TO STATE
 * `campaignSendTimes` (whatsapp-scheduler-backend
 * `src/services/actions/actions/campaign-actions.ts`) gives contact *i* a due date
 * of `campaignDueDate + 7 * i` seconds. The spacing is HARDCODED — the `timing`
 * argument is accepted and never read — and it is unconditional: both engines
 * honour it (the extension through `can-i-send`, the Baileys pod through its
 * campaign poll loop). So a 500-contact campaign takes ~58 minutes and a
 * 2,000-contact campaign takes nearly four hours, while the wizard's Review step
 * said "Sends as soon as you press send."
 *
 * The wizard's pacing CONTROLS were hidden deliberately (`SHOW_GLOBAL_LIMITERS`)
 * and hiding them was right: they only ever set local state and the window they
 * promised is unimplemented. Showing them back would re-create that lie. What was
 * missing is not a control — it is the plain statement of what already happens.
 * That is what this computes.
 *
 * Kept pure and dependency-free so the arithmetic is testable and so the ONE
 * place that carries the 7 is here, next to a pointer at the line it mirrors. If
 * the backend spacing ever changes, this file is what a `grep 7` finds.
 */

import {
    CAMPAIGN_RANDOM_GAP_MIN_SECONDS,
    CAMPAIGN_RANDOM_GAP_MAX_SECONDS,
    type CampaignRandomGap,
} from '@bt-common/schemas/bt/campaign';

/**
 * Seconds between one contact's send and the next, on the real send path.
 *
 * NOTE — `bt-common/schemas/bt/campaign.ts` documents `messageInterval` as
 * "IMPLEMENTED — per-message send delay (default 8s)". That comment is wrong on
 * both counts: nothing on the send path reads `messageInterval`, and the real
 * spacing is 7. Do not take the number from there.
 */
export const CAMPAIGN_SEND_SPACING_SECONDS = 7;

// #412 — OPTIONAL randomized gap. The bounds live in bt-common (the backend validates against the
// same constants); re-exported here so the wizard has one import for pacing.
export {
    CAMPAIGN_RANDOM_GAP_MIN_SECONDS,
    CAMPAIGN_RANDOM_GAP_MAX_SECONDS,
} from '@bt-common/schemas/bt/campaign';
export type { CampaignRandomGap } from '@bt-common/schemas/bt/campaign';

/**
 * Seconds from the first send to the last, for `contacts` recipients.
 *
 * The FIRST contact is not delayed (i = 0), so the span is one gap short of the
 * head count — an off-by-one that matters at the small sizes where the sentence
 * is most likely to be read: two contacts take 7 seconds, not 14.
 */
export const campaignSendSpanSeconds = (contacts: number): number => {
    if (!Number.isFinite(contacts) || contacts <= 1) return 0;
    return (Math.floor(contacts) - 1) * CAMPAIGN_SEND_SPACING_SECONDS;
};

/**
 * The span expressed in the largest unit that does not make the number silly.
 *
 * TWO ROUNDING RULES, AND WHY THEY DIFFER
 * Minutes round UP: this sentence exists to stop someone being surprised, and
 * "1 minute" for something that takes 63 seconds is the original defect wearing
 * a number.
 * Hours round to NEAREST, and we do not reach for hours until the span is at
 * least two of them. Ceiling into hours the moment the span crosses 60 minutes
 * turns 1h00m05s into "about 2 hours" — an over-statement of almost an hour,
 * which is just the same lie pointing the other way. Under two hours the answer
 * is given in minutes, where a whole minute of slack is negligible.
 *
 * `unit` is returned rather than a formatted string so the caller can hand the
 * value to i18next and let each locale inflect it (Hebrew needs its dual for
 * two minutes / two hours; English does not).
 */
export type PacingUnit = 'second' | 'minute' | 'hour';

export interface PacingSpan {
    unit: PacingUnit;
    /** Always a whole number ≥ 1 when `seconds > 0`. */
    value: number;
    /** The raw span, so a caller can decide not to say anything at all. */
    seconds: number;
}

export const describeCampaignSendSpan = (contacts: number): PacingSpan => {
    const seconds = campaignSendSpanSeconds(contacts);
    if (seconds <= 0) return { unit: 'second', value: 0, seconds: 0 };
    if (seconds < 60) return { unit: 'second', value: seconds, seconds };
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 120) return { unit: 'minute', value: minutes, seconds };
    return { unit: 'hour', value: Math.round(minutes / 60), seconds };
};

/**
 * Is the pacing worth a sentence at all?
 *
 * Below a minute the pacing is invisible to a human and saying so is noise that
 * trains people to skip the line — which would cost us the 2,000-contact case,
 * the one it exists for. One minute is the threshold because it is the first
 * span a person would notice while watching.
 */
export const pacingIsWorthStating = (contacts: number): boolean =>
    campaignSendSpanSeconds(contacts) >= 60;

// ════════ #412 — optional randomized gap ════════

/** Which field of an in-progress min/max entry is wrong, if any. `null` = valid. */
export type RandomGapFieldError = 'min' | 'max' | 'order' | null;

/**
 * Validate the two inputs exactly as the backend does (whole seconds, min ≥ 5, max ≤ 120,
 * min ≤ max). Takes raw numbers; the inputs hand over NaN while a field is empty.
 */
export const randomGapFieldError = (minSeconds: number, maxSeconds: number): RandomGapFieldError => {
    const bad = (n: number) => !Number.isInteger(n);
    if (bad(minSeconds) || minSeconds < CAMPAIGN_RANDOM_GAP_MIN_SECONDS || minSeconds > CAMPAIGN_RANDOM_GAP_MAX_SECONDS) return 'min';
    if (bad(maxSeconds) || maxSeconds < CAMPAIGN_RANDOM_GAP_MIN_SECONDS || maxSeconds > CAMPAIGN_RANDOM_GAP_MAX_SECONDS) return 'max';
    if (minSeconds > maxSeconds) return 'order';
    return null;
};

/** Defaults offered when the toggle is switched on. */
export const DEFAULT_RANDOM_GAP = { minSeconds: 5, maxSeconds: 15 } as const;

export interface PacingRange {
    unit: PacingUnit;
    /** Shortest / longest plausible total, in `unit`, both whole numbers. */
    minValue: number;
    maxValue: number;
    minSeconds: number;
    maxSeconds: number;
}

/**
 * The span for `contacts` recipients when each of the (contacts − 1) gaps is uniform in
 * [min, max]: it can fall anywhere between (n−1)·min and (n−1)·max, and in practice sits near
 * (n−1)·(min+max)/2. The sentence states the full range (honest bounds), in one shared unit
 * picked from the SHORTER end so both numbers read in the same unit ("50–80 minutes").
 */
/** Longest upper end still stated in seconds when the quickest case is under a minute. */
const SECONDS_RANGE_MAX_SECONDS = 120;

export const describeCampaignSendRange = (contacts: number, gap: CampaignRandomGap): PacingRange => {
    const gaps = Number.isFinite(contacts) && contacts > 1 ? Math.floor(contacts) - 1 : 0;
    const minSeconds = gaps * gap.minSeconds;
    const maxSeconds = gaps * gap.maxSeconds;
    // Unit from the SHORTER end (hours only when even the quickest outcome is ≥ 2h): "41–125 minutes"
    // beats "1–3 hours", whose lower end would overstate the quickest case. A sub-minute lower end
    // stays in seconds while the upper end is at most two minutes: 5 contacts at 5–15s is
    // "20–60 seconds", where minutes would floor/ceil it to a meaningless "0–1 minutes".
    const unit = minSeconds < 60
        ? (maxSeconds <= SECONDS_RANGE_MAX_SECONDS ? 'second' : 'minute')
        : describeUnit(minSeconds);
    const toUnit = (sec: number, round: (n: number) => number) =>
        unit === 'second' ? sec : unit === 'minute' ? round(sec / 60) : round(sec / 3600);
    return {
        unit,
        // Lower end rounds down, upper end rounds up, so the stated range always contains the truth.
        // 0 is allowed: 2 contacts at 5–120s is truly "0–2 minutes" (quickest case is 5s).
        minValue: Math.max(0, toUnit(minSeconds, Math.floor)),
        maxValue: Math.max(1, toUnit(maxSeconds, Math.ceil)),
        minSeconds,
        maxSeconds,
    };
};

const describeUnit = (seconds: number): PacingUnit => {
    if (seconds < 60) return 'second';
    if (seconds < 7200) return 'minute';
    return 'hour';
};
